/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { AddressResource, PageQueryParams, PeriodQueryParams, SearchQueryParams } from '../apiCommon.typings';
import { DocType } from '../documents/documentsClient.typings';
import { StoreType, storeStatus } from '../stores/storesClientTypings';
import { companiesStatus } from '../companies/companiesClient.typings';
import { Viewport } from '../../../components/Map/typings';
import {
    SoftwareVersionItemDto,
    SoftwareVersionStatus,
    VersionStatus,
} from '../cashRegisterModels/cashRegisterModelsClient.typings';

export interface CashBoxDetailsDto {
    taxNumber: string;
    cashBoxType: CashBoxTypes;
    companyTaxNumber: string;
    companyName: string;
    storeTaxNumber: number;
    operatorTaxNumber: number;
    operatorName: string;
    storeName: string;
    storeType: string;
    storeAddress: AddressResource;
    storeFullAddress: string;
    online: boolean;
    validTime: boolean;
    status: CashBoxesStatus;
    firmwareVersion: string;
    temporary: boolean;
    timeDelta?: number;
    taxationTypes: string[];
    storeTaxAuthority: string;
    statusComment: string;
    statusDateAtUtc: string;
    blockEndAtUtc: string;
    blockEndComment: string;
    blockStartAtUtc: string;
    blockStartComment: string;
    cashRegisterModelName: string;
    cashRegisterModelTaxNumber: number;
    cashRegisterFactoryNumber: string;
    fiscalDriverVersion: string;
    storeStatus: storeStatus;
    companyStatus: companiesStatus;
    os: string;
    pingDateAtUtc: string;
    deviceFactoryNumber: string;
    certificateEndDateAtUtc: string;
    registeredDateAtUtc: string;
    auditLastDateAtUtc: string;
    softwareVersion?: string;
    softwareVersionStatus?: VersionStatus;
    portable?: boolean;
    storeWebSite?: string;
    storeMainActivity?: string;
    storeMiddleActivity?: string;
    storeLowerActivity?: string;
}

export interface CashBoxListItemDto {
    taxNumber: string;
    companyTaxNumber: string;
    storeTaxNumber: string;
    storeName: string;
    storeType: StoreType;
    storeAddress: AddressResource;
    storeFullAddress: string;
    storeTaxAuthority: string;
    online: boolean;
    validTime: boolean;
    status: CashBoxesStatus;
    firmwareVersion: string;
    fiscalDriverVersion: string;
    temporary: boolean;
    timeDelta?: number;
    taxationTypes: string[];
    cashRegisterFactoryNumber: string;
    cashBoxType: CashBoxTypes;
    storeMainActivity?: string;
    storeMiddleActivity?: string;
    storeLowerActivity?: string;
}

export interface CashBoxesListDto {
    afterKeyTaxNumber: string;
    cashBoxes: CashBoxListItemDto[];
}

export interface CashBoxesListPageQueryParams {
    afterKeyTaxNumber: string;
    limit?: number;
}

export interface CashBoxesListFilterParams extends Partial<SearchQueryParams> {
    state: CashBoxeState;
    temporary?: boolean;
    cashBoxTypes?: CashBoxTypes[];
    gpsTracking?: boolean;
    portable?: boolean;
    cashBoxStatuses?: CashBoxesStatus[];
    softwareVersionStatuses?: SoftwareVersionStatus[];
    softwareVersions?: SoftwareVersionItemDto[];

    fromRegisteredAtUtc?: string;
    toRegisteredAtUtc?: string;
    fromOffline?: number | string;
    toOffline?: number | string;
    fromLastDocument?: number | string;
    toLastDocument?: number | string;
    fromExpiredCert?: number | string;
    toExpiredCert?: number | string;

    fromTimeDelta?: number | string;
    toTimeDelta?: number | string;

    cashRegisterModelTaxNumbers?: string[];
    operatorTaxNumbers?: string[];
    firmwareVersions?: string[];
    fiscalDriverVersions?: string[];
    taxationTypes?: string[];
    storeTaxAuthorityIds?: string[];
    regionCodes: string[];

    storeTypes?: string[];
    storeMainActivities?: string[];
    storeMiddleActivities?: string[];
    storeLowerActivities?: string[];
}

export type CashBoxesListRequestParams = CashBoxesListPageQueryParams & CashBoxesListFilterParams;
export type CashBoxesListExcelRequestParams = CashBoxesListFilterParams;

export enum CashBoxeState {
    ONLINE = 'ONLINE',
    OFFLINE = 'OFFLINE',
}

export enum ReplacementType {
    PRIMARY = 'PRIMARY',
    TEMPORARY = 'TEMPORARY',
}

export enum CashBoxTypes {
    PHYSICAL = 'PHYSICAL',
    VIRTUAL = 'VIRTUAL',
    SOFTWARE = 'SOFTWARE',
    ECOMM = 'ECOMM',
}

export const CASH_BOXES_STATUSES = [
    'REGISTRATION',
    'ACTIVE',
    'BLOCKED',
    'RESET',
    'BROKEN',
    'SERVICE',
    'ELIMINATED',
] as const;

export type CashBoxesStatus = (typeof CASH_BOXES_STATUSES)[number];

export interface ChangeCashBoxStatusParams {
    status: CashBoxesStatus;
    comment: string;
}

export interface BlockCashBoxQueryParams {
    comment: string;
    endDate?: string;
    startDate?: string;
}

export interface UnblockCashBoxQueryParams {
    comment: string;
    startDate?: string;
}

// -----Documents history----- //

export type CashBoxDocumentsListFilterParams = {
    docType?: string;
    dateFrom: string;
    dateTo: string;
};

export type LoadCashBoxDocsFileParams = {
    docType: string;
    from_utc: string;
    to_utc: string;
    taxNumber: string;
};

export interface CashBoxLastDocument {
    docTypeId: DocType;
    createdAtUtc: string;
    insertedDateAtUtc: string;
    sum: number;
    docNumber: number; //z-report, openShift = reportNumber
    globalDocNumber: number;
}

// -----Statuses history----- //

export type CashBoxStatusesHistoryQueryParams = PageQueryParams;

export interface CashBoxStatusDto {
    status: CashBoxesStatus;
    dateAtUtc: string;
    duration: number;
    userName?: string;
    comment?: string;
}

export interface CashBoxStatusesHistoryDto {
    count: number;
    statuses: CashBoxStatusDto[];
}

// -----TimeDelta history----- //

export type CashBoxTimeDeltaHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface CashBoxTimeDeltaDto {
    validTime: boolean;
    dateAtUtc: string;
    timeDelta: number | null;
    duration: number | null;
}

export interface CashBoxTimeDeltaHistoryDto {
    count: number;
    timeDeltas: CashBoxTimeDeltaDto[];
}

// -----State history----- //

export type CashBoxStateHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface CashBoxStateDto {
    state: 'ONLINE' | 'OFFLINE';
    dateAtUtc: string;
    duration: number;
}

export interface CashBoxStateHistoryDto {
    count: number;
    states: CashBoxStateDto[];
}

//-----Audit history-----//

export type CashBoxAuditHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface AuditDto {
    status: 'SUCCESS' | 'FAILED';
    date: string;
    details: string;
}

export interface AuditBaseDto {
    year: number;
    quarter: number;
}

export interface CashBoxAuditHistoryItemDto extends AuditBaseDto {
    audits: AuditDto[];
}
export interface CashBoxAuditHistoryDto {
    count: number;
    audits: CashBoxAuditHistoryItemDto[];
}

//-----Replacement history-----//

export interface CashBoxReplacementDto {
    taxNumber: string;
    cashRegisterFactoryNumber: string;
    status: CashBoxesStatus;
    cashRegisterModelName: string;
    registrationDateAtUtc: string;
    temporary: boolean;
    certificateExpirationDateAtUtc: string;
}

export interface CashBoxReplacementHistoryDto {
    parents: CashBoxReplacementDto[];
    children: CashBoxReplacementDto[];
}

//-----Alerts history-----//

export type CashBoxAlertsHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export type CashBoxAlertsHistoryExcelParams = {
    month: string;
};

export interface CashBoxAlertsDto {
    month: string;
    minor: number;
    major: number;
    normal: number;
    count: number;
}

export interface CashBoxAlertsHistoryDto {
    count: number;
    alerts: CashBoxAlertsDto[];
}

// -----Cashboxes map----- // //TODO: move to map data typing

export interface CashBoxesMapFilterParams extends SearchQueryParams {
    afterKeyId?: string;
    limit?: number;
    cashBoxStatuses?: CashBoxesStatus[];
}

export interface CashBoxesMapSearchParams {
    viewport: Viewport;
    filterParams: CashBoxesMapFilterParams;
}

export interface Claster {
    count: number;
    geoHash: string;
}

export interface CashBoxPoint {
    taxNumber: string;
    lat: number;
    lon: number;
}

export interface CashBoxesMap {
    afterKeyId?: string;
    clusters: Claster[];
    points: CashBoxPoint[];
}

export interface CashBoxDetailsForMap {
    taxNumber: string;
    online: boolean;
    gpsUpdatedDateAtUtc: string;
}

//-----GPS tracking tab-----//

export interface CashboxLocationData {
    lat: number;
    lon: number;
    gpsUpdatedAtUtc: string;
    storeLat: number;
    storeLon: number;
}

export interface LocationsListItem {
    lat: number;
    lon: number;
    insertedDateAtUtc: string;
}

export interface HistoryLocationsList {
    count: number;
    items: LocationsListItem[];
}

export interface GpsTrackingLocationsList {
    accuracy: number;
    items: LocationsListItem[];
}

export interface HistoryLocationsFilterParams {
    fromInsertedDateAtUtc: string;
    toInsertedDateAtUtc: string;
}

export type HistoryLocationsQueryParams = HistoryLocationsFilterParams & PageQueryParams;

//-----Checksums history-----//

export interface CheckSumsHistoryItemDto {
    softwareVersion: string;
    softwareVersionStatus: VersionStatus;
    insertedDateAtUtc: string;
    updatedDateAtUtc: string;
    fiscalDriverVersion: string;
    firmwareVersion: string;
    checksum: string;
}

export interface CheckSumsHistoryDto {
    count: number;
    items: CheckSumsHistoryItemDto[];
}

//-----Cashboxes statistics-----//

export interface CashBoxesStatsStates {
    active: number;
    blocked: number;
    inactive: number;
    notBlocked: number;
    offline: number;
    online: number;

    [key: string]: number;
}

export interface CashBoxesStatsStatuses {
    registration: number;
    active: number;
    blocked: number;
    reset: number;
    broken: number;
    service: number;
    eliminated: number;

    [key: string]: number;
}

export interface CashBoxesStatsTypes {
    temporary: number;
    primary: number;

    [key: string]: number;
}

export interface CashBoxesStatsTimer {
    correct: number;
    incorrect: number;

    [key: string]: number;
}

export interface CashBoxesStatsCertificates {
    valid: number;
    expired: number;

    [key: string]: number;
}

export interface CashBoxesStats {
    states: CashBoxesStatsStates;
    statuses: CashBoxesStatsStatuses;
    types: CashBoxesStatsTypes;
    timers: CashBoxesStatsTimer;

    [key: string]:
        | CashBoxesStatsStates
        | CashBoxesStatsStatuses
        | CashBoxesStatsTypes
        | CashBoxesStatsTimer
        | CashBoxesStatsCertificates;
}

export interface OperatorCashBoxesStats extends CashBoxesStats {
    certificates: CashBoxesStatsCertificates;
}
